import {
  ENTITY_ICON_CATALOG,
  ICON_COLOR_LABELS,
  ICON_COLOR_KEYS,
  isIconColorKey,
  type EntityIconSlug,
  type IconColorKey,
  type IconConfig,
} from '@littlebird/sdk';

import { Icons, type IconName, type LBIcon } from '@/assets/icons/icons';

export interface EntityIconColor {
  key: IconColorKey;
  label: string;
  fg: string;
  bg: string;
  bgSubtle: string;
  border: string;
}

export const ICON_COLORS: EntityIconColor[] = ICON_COLOR_KEYS.map((key) => ({
  key,
  label: ICON_COLOR_LABELS[key],
  fg: `var(--${key}-11)`,
  bg: `var(--${key}-4)`,
  bgSubtle: `var(--${key}-3)`,
  border: `var(--${key}-a2)`,
}));

type EntityIconName = {
  [Key in IconName]: (typeof Icons)[Key] extends LBIcon ? Key : never;
}[IconName];

const ENTITY_ICON_COMPONENTS = {
  folder: 'folder',
  'folder-open': 'folderOpen',
  file: 'file',
  'file-text': 'textFile',
  notepad: 'notepad',
  'sticky-note': 'stickyNote',
  book: 'book',
  archive: 'archive',
  inbox: 'inbox',
  tag: 'tag',
  pin: 'pin',
  link: 'link',
  lock: 'lock',
  'layout-grid': 'layoutGrid',
  briefcase: 'briefcase',
  building: 'building',
  presentation: 'presentation',
  'chart-bar': 'chartBar',
  'chart-line': 'chartLine',
  'chart-pie': 'chartPie',
  target: 'target',
  trophy: 'trophy',
  award: 'award',
  flag: 'flag',
  rocket: 'rocket',
  crown: 'crown',
  gem: 'gem',
  handshake: 'handshake',
  megaphone: 'megaphone',
  wallet: 'wallet',
  'graduation-cap': 'graduationCap',
  code: 'code',
  terminal: 'terminal',
  bug: 'bug',
  database: 'database',
  server: 'server',
  cpu: 'cpu',
  laptop: 'laptop',
  monitor: 'monitor',
  smartphone: 'smartphone',
  cloud: 'cloud',
  wifi: 'wifi',
  bot: 'bot',
  brain: 'brain',
  zap: 'zap',
  settings: 'settings',
  wrench: 'wrench',
  hammer: 'hammer',
  shield: 'shield',
  scissors: 'scissors',
  palette: 'palette',
  key: 'key',
  users: 'users',
  user: 'user',
  chat: 'chat',
  speech: 'speech',
  mail: 'mail',
  send: 'paperPlane',
  phone: 'phone',
  video: 'video',
  mic: 'mic',
  headphones: 'headphones',
  home: 'home',
  heart: 'heart',
  star: 'star',
  smile: 'smile',
  coffee: 'coffee',
  utensils: 'utensils',
  pizza: 'pizza',
  cake: 'cake',
  gift: 'gift',
  'shopping-bag': 'shoppingBag',
  'shopping-cart': 'shoppingCart',
  shirt: 'shirt',
  dumbbell: 'dumbbell',
  bike: 'bike',
  music: 'music',
  gamepad: 'gamepad',
  camera: 'camera',
  film: 'film',
  ticket: 'ticket',
  leaf: 'leaf',
  flower: 'flower',
  sprout: 'sprout',
  tree: 'tree',
  sun: 'sun',
  moon: 'moon',
  snowflake: 'snowflake',
  flame: 'flame',
  droplet: 'droplet',
  waves: 'waves',
  mountain: 'mountain',
  dog: 'dog',
  compass: 'compass',
  map: 'map',
  'map-pin': 'mapPin',
  globe: 'globe',
  plane: 'plane',
  car: 'car',
  truck: 'truck',
  ship: 'ship',
  stethoscope: 'stethoscope',
  microscope: 'microscope',
  flask: 'flask',
  atom: 'atom',
  telescope: 'telescope',
  lightbulb: 'lightbulb',
  clock: 'clock',
  calendar: 'calendar',
  'calendar-days': 'calendarDays',
  'alarm-clock': 'alarmClock',
  bell: 'bell',
  history: 'history',
  sparkles: 'sparkles',
  pencil: 'pencil',
  play: 'playAlt',
  image: 'image',
  'hand-coins': 'handCoins',
  'chart-no-axes-combined': 'chartNoAxesCombined',
  landmark: 'landmark',
  'briefcase-business': 'briefcaseBusiness',
  package: 'package',
  banknote: 'banknote',
  scale: 'scale',
  gauge: 'gauge',
  'list-checks': 'listChecks',
  'shopping-basket': 'shoppingBasket',
  'wand-sparkles': 'wandSparkles',
  'party-popper': 'partyPopper',
  'hat-glasses': 'hatGlasses',
  'pen-line': 'penLine',
  'clock-fading': 'clockFading',
  'piggy-bank': 'piggyBank',
  swords: 'swords',
  'paw-print': 'pawPrint',
  puzzle: 'puzzle',
  'chef-hat': 'chefHat',
  'biceps-flexed': 'bicepsFlexed',
  'life-buoy': 'lifeBuoy',
  'tree-palm': 'treePalm',
  cookie: 'cookie',
  trees: 'trees',
  ghost: 'ghost',
  fish: 'fish',
  'tent-tree': 'tentTree',
  'chess-knight': 'chessKnight',
  'git-pull-request': 'gitPullRequest',
  guitar: 'guitar',
  piano: 'piano',
  feather: 'feather',
  bird: 'bird',
  birdhouse: 'birdhouse',
  'pencil-sparkles': 'pencilSparkles',
  'face-slightly-smiling': 'faceSmiling',
  'mic-audio-lines': 'micAudioLines',
} satisfies Record<EntityIconSlug, EntityIconName>;

export interface EntityIconEntry {
  slug: EntityIconSlug;
  icon: EntityIconName;
  keywords: string;
}

const COMPONENT_BY_SLUG = new Map<string, EntityIconName>(
  Object.entries(ENTITY_ICON_COMPONENTS)
);

export const ICONS: EntityIconEntry[] = ENTITY_ICON_CATALOG.map((entry) => ({
  ...entry,
  icon: ENTITY_ICON_COMPONENTS[entry.slug],
}));

export type ResolvedEntityIcon =
  | { kind: 'icon'; Icon: LBIcon }
  | { kind: 'emoji'; emoji: string };

export function resolveEntityIcon(
  icon: IconConfig | null | undefined
): ResolvedEntityIcon {
  if (icon?.type === 'emoji' && icon.code) {
    return { kind: 'emoji', emoji: icon.code };
  }

  const component = icon?.code ? COMPONENT_BY_SLUG.get(icon.code) : undefined;
  return { kind: 'icon', Icon: component ? Icons[component] : Icons.folder };
}

export interface ResolvedIconColor {
  fg: string;
  bg: string;
  bgSubtle: string;
  border: string;
  isDefault: boolean;
}

const DEFAULT_ICON_COLOR: ResolvedIconColor = {
  fg: 'var(--foreground-muted)',
  bg: 'var(--surface-active)',
  bgSubtle: 'var(--surface-hover)',
  border: 'var(--gray-a2)',
  isDefault: true,
};

export function iconTintStyle(
  color: string | null | undefined,
  subtle = false
): { backgroundColor: string; borderColor: string } {
  const resolved = resolveEntityIconColor(color);
  return {
    backgroundColor: subtle ? resolved.bgSubtle : resolved.bg,
    borderColor: resolved.border,
  };
}

export function resolveEntityIconColor(
  color: string | null | undefined
): ResolvedIconColor {
  if (isIconColorKey(color)) {
    const paletteColor = ICON_COLORS.find(
      (candidate) => candidate.key === color
    );
    if (paletteColor) {
      return {
        fg: paletteColor.fg,
        bg: paletteColor.bg,
        bgSubtle: paletteColor.bgSubtle,
        border: paletteColor.border,
        isDefault: false,
      };
    }
  }
  return DEFAULT_ICON_COLOR;
}
